import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

export type ConnectionStatus = 'checking' | 'connected' | 'offline' | 'unreachable';

@Injectable({ providedIn: 'root' })
export class ConnectivityService implements OnDestroy {
  private readonly statusSubject = new BehaviorSubject<ConnectionStatus>('checking');
  readonly status$ = this.statusSubject.asObservable();
  readonly online = toSignal(this.status$.pipe(
    map(status => status === 'connected' || status === 'checking' && globalThis.navigator?.onLine === true),
  ), { initialValue: false });

  private readonly probeUrl = '/identity/health/ready?ngsw-bypass=true';
  private readonly probeIntervalMs = 10000;
  private readonly probeTimeoutMs = 4000;
  private intervalId?: number;
  private activeProbe?: AbortController;
  private probeAfterCurrent = false;
  private failedProbes = 0;
  private started = false;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly ngZone: NgZone
  ) {}

  start(): void {
    const windowRef = this.document.defaultView;
    if (this.started || !isPlatformBrowser(this.platformId) || !windowRef) {
      return;
    }

    this.started = true;
    this.ngZone.runOutsideAngular(() => {
      windowRef.addEventListener('online', this.handleOnline);
      windowRef.addEventListener('offline', this.handleOffline);
      this.document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.intervalId = windowRef.setInterval(() => { void this.probe(); }, this.probeIntervalMs);

      if (windowRef.navigator.onLine) {
        void this.probe();
      } else {
        this.updateStatus('offline');
      }
    });
  }

  stop(): void {
    if (!this.started) {
      return;
    }

    this.started = false;
    this.probeAfterCurrent = false;
    const windowRef = this.document.defaultView;
    windowRef?.removeEventListener('online', this.handleOnline);
    windowRef?.removeEventListener('offline', this.handleOffline);
    this.document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    if (this.intervalId !== undefined) {
      windowRef?.clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
    this.activeProbe?.abort();
  }

  ngOnDestroy(): void {
    this.stop();
    this.statusSubject.complete();
  }

  private readonly handleOnline = (): void => {
    this.failedProbes = 0;
    this.updateStatus('checking');
    if (this.activeProbe) {
      this.probeAfterCurrent = true;
      this.activeProbe.abort();
    } else {
      void this.probe();
    }
  };

  private readonly handleOffline = (): void => {
    this.failedProbes = 0;
    this.activeProbe?.abort();
    this.updateStatus('offline');
  };

  private readonly handleVisibilityChange = (): void => {
    if (!this.document.hidden) {
      void this.probe();
    }
  };

  private async probe(): Promise<void> {
    const windowRef = this.document.defaultView;
    if (!this.started || !windowRef || this.activeProbe) {
      return;
    }
    if (!windowRef.navigator.onLine) {
      this.updateStatus('offline');
      return;
    }

    const controller = new AbortController();
    this.activeProbe = controller;
    const timeoutId = windowRef.setTimeout(() => controller.abort(), this.probeTimeoutMs);

    try {
      const response = await windowRef.fetch(this.probeUrl, {
        cache: 'no-store',
        headers: { 'ngsw-bypass': 'true' },
        signal: controller.signal,
      });
      if (!this.started || this.probeAfterCurrent) {
        return;
      }
      if (!windowRef.navigator.onLine) {
        this.updateStatus('offline');
        return;
      }
      if (response.ok) {
        this.failedProbes = 0;
        this.updateStatus('connected');
      } else {
        this.recordFailure();
      }
    } catch {
      if (!this.started || this.probeAfterCurrent) {
        return;
      }
      if (!windowRef.navigator.onLine) {
        this.updateStatus('offline');
      } else {
        this.recordFailure();
      }
    } finally {
      windowRef.clearTimeout(timeoutId);
      if (this.activeProbe === controller) {
        this.activeProbe = undefined;
      }
      if (this.probeAfterCurrent && this.started) {
        this.probeAfterCurrent = false;
        void this.probe();
      }
    }
  }

  private recordFailure(): void {
    this.failedProbes += 1;
    if (this.failedProbes >= 2) {
      this.updateStatus('unreachable');
    }
  }

  private updateStatus(status: ConnectionStatus): void {
    if (this.statusSubject.value !== status) {
      this.ngZone.run(() => this.statusSubject.next(status));
    }
  }
}
